import React, { Component } from 'react';

/**
 * 关闭状态下的浮动按钮旁挂气泡消息。
 *
 * 设计目标：不打扰但能暗示"可以聊天" —— 参考 Intercom / Drift / Crisp 的 teaser 气泡。
 * - PC：挂在按钮左侧（按钮靠右下时的自然视觉延伸）
 * - Mobile：挂在按钮上方（避免挤占左侧内容区）
 * - 点击气泡 → 触发 onClick（和按钮一致，展开 chat）
 * - 右上角小 × → 只 dismiss teaser 不展开，写 localStorage 避免同会话复现
 *
 * 不渲染不订阅：父组件控制 visible/shouldMount，保证零性能开销。
 */
export default class ChatTeaserBubble extends Component {
    constructor(props) {
        super(props);
        this.state = { mounted: false };
    }

    componentDidMount() {
        // 下一帧加 mounted 类触发滑入动画（避免首帧直接就在终态）
        requestAnimationFrame(() => this.setState({ mounted: true }));
    }

    handleClick = (e) => {
        if (this.props.onClick) this.props.onClick(e);
    }

    handleDismiss = (e) => {
        e.stopPropagation();
        if (this.props.onDismiss) this.props.onDismiss();
    }

    render() {
        const { message, title, avatarUrl, isMobile, color } = this.props;
        const { mounted } = this.state;

        // PC：按钮在右下角 → teaser 挂左侧；Mobile：挂正上方
        // 位置数字要和浮动按钮的定位对齐（btnPcBottom/btnPcRight），通过 inset 相对父 wrapper 计算
        const wrapperStyle = isMobile
            ? {
                position: 'absolute',
                right: 0,
                bottom: 72,               // 按钮高度 56 + 间隙 16
                maxWidth: 'calc(100vw - 32px)',
                pointerEvents: 'auto',
            }
            : {
                position: 'absolute',
                right: 72,                // 按钮宽度 56 + 间隙 16
                bottom: 4,
                maxWidth: 280,
                pointerEvents: 'auto',
            };

        const bubbleStyle = {
            position: 'relative',
            background: 'var(--lc-surface, #fff)',
            color: 'var(--lc-text, #1e293b)',
            // Size to content up to a cap — the wrapper is absolutely positioned
            // inside the narrow FAB container, so without this the bubble collapses
            // to a sliver and wraps one word per line.
            width: 'max-content',
            maxWidth: isMobile ? 'calc(100vw - 32px)' : 280,
            borderRadius: 'var(--lc-radius-lg)',
            padding: avatarUrl ? '12px 36px 12px 48px' : '12px 36px 12px 14px',
            boxShadow: 'var(--lc-shadow-3)',
            fontSize: 13.5,
            lineHeight: 1.5,
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            cursor: 'pointer',
            transform: mounted ? 'translateY(0)' : 'translateY(8px)',
            opacity: mounted ? 1 : 0,
            transition: 'transform var(--lc-dur-slow) var(--lc-ease-out), opacity var(--lc-dur-slow) var(--lc-ease-out)',
        };

        // Tail 三角：PC 指向右（朝按钮），Mobile 指向下
        const tailStyle = isMobile
            ? {
                position: 'absolute',
                right: 18,
                bottom: -6,
                width: 12, height: 12,
                background: 'var(--lc-surface, #fff)',
                transform: 'rotate(45deg)',
                boxShadow: '2px 2px 4px rgba(15,23,42,0.06)',
            }
            : {
                position: 'absolute',
                right: -6,
                bottom: 18,
                width: 12, height: 12,
                background: 'var(--lc-surface, #fff)',
                transform: 'rotate(45deg)',
                boxShadow: '2px -2px 4px rgba(15,23,42,0.06)',
            };

        return (
            <div style={wrapperStyle}
                 role="button"
                 tabIndex={0}
                 onClick={this.handleClick}
                 onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.handleClick(e); } }}
                 aria-label={message}
            >
                <div style={bubbleStyle}>
                    {avatarUrl && (
                        <img src={avatarUrl} alt=""
                             style={{
                                 position: 'absolute',
                                 left: 10, top: '50%',
                                 transform: 'translateY(-50%)',
                                 width: 30, height: 30,
                                 borderRadius: 'var(--lc-radius-full)',
                                 objectFit: 'cover',
                                 border: `2px solid ${color || '#2563eb'}`,
                             }} />
                    )}
                    {title && (
                        <div style={{ fontWeight: 600, marginBottom: 2, color: 'var(--lc-text, #0f172a)' }}>{title}</div>
                    )}
                    <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{message}</div>
                    <button
                        onClick={this.handleDismiss}
                        aria-label="Dismiss"
                        style={{
                            position: 'absolute',
                            top: 6, right: 6,
                            width: 20, height: 20,
                            borderRadius: 'var(--lc-radius-full)',
                            border: 'none',
                            background: 'rgba(15, 23, 42, 0.06)',
                            color: 'var(--lc-text-2, #64748b)',
                            cursor: 'pointer',
                            fontSize: 12,
                            lineHeight: '20px',
                            padding: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                    >
                        ×
                    </button>
                    <div style={tailStyle} aria-hidden="true" />
                </div>
            </div>
        );
    }
}
